<script setup lang="ts">
import { faIconStyle } from '~/utils/faIconStyle'
const { getValues } = useSchool()
const { data: values } = await useAsyncData('school-values', () => getValues())

// Only one card can be flipped at a time
const flippedIndex = ref<number | null>(null)
const toggleFlip = (index: number) => {
  flippedIndex.value = flippedIndex.value === index ? null : index
}
const isFlipped = (index: number) => flippedIndex.value === index

const { elementRef: sectionRef, isVisible: isVisible } = useScrollReveal()
</script>

<template>
  <section id="valeurs" ref="sectionRef" class="py-section-py bg-background text-foreground relative overflow-hidden transition-colors duration-300">
    <div class="container mx-auto px-container-px relative z-10">
      <div class="flex flex-col lg:flex-row items-center gap-16 lg:gap-32">
        <!-- Title Side -->
        <div class="lg:w-2/5 transition-all duration-1000" :class="isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'">
          <UiSectionHeader 
            titlePrefix="Nos"
            titleHighlight="Valeurs"
            subtitle="Elles ne sont pas seulement affichées sur nos murs, elles vivent à travers chaque projet et chaque étudiant."
            align="left"
          />
        </div>

        <!-- Values Grid -->
        <div class="lg:w-3/5 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div 
            v-for="(value, index) in values" 
            :key="index"
            class="group perspective-1000 h-[280px] cursor-pointer w-[300px] max-w-full flex-none mx-auto xl:mx-0"
            :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'"
            :style="{ transitionDelay: (400 + (index * 150)) + 'ms' }"
            @click="toggleFlip(index)"
          >
            <!-- Flip Container -->
            <div 
              class="relative w-full h-full transition-transform duration-700 preserve-3d"
              :class="{ 'rotate-y-180': isFlipped(index) }"
            >
              <!-- Front Side -->
              <div class="absolute inset-0 backface-hidden">
                <div class="h-full relative bg-oxo-card-bg border border-oxo-border p-10 rounded-3xl flex flex-col items-center justify-center transition-all duration-500 group-hover:bg-oxo-card-bg/80 group-hover:border-oxo-border/50">
                  <!-- Decorative Background behind card -->
                  <div class="absolute inset-0 bg-linear-to-br from-oxo-red to-oxo-yellow opacity-0 group-hover:opacity-10 blur-xl transition-all duration-500 scale-90 group-hover:scale-100"></div>
                  
                  <!-- Icon Container -->
                  <div class="relative mb-6">
                    <div class="w-16 h-16 rounded-2xl bg-oxo-card-bg/40 flex items-center justify-center text-3xl text-oxo-red dark:text-white group-hover:text-oxo-yellow transition-colors duration-500">
                      <i :style="faIconStyle('fas fa-' + value.icon.replace('fa-', '').replace('fas ', ''))" :class="'fas fa-' + value.icon.replace('fa-', '').replace('fas ', '')" aria-hidden="true"></i>
                    </div>
                  </div>

                  <h3 class="font-display font-black text-2xl mb-4 uppercase group-hover:text-oxo-red transition-colors duration-300 text-center text-foreground">
                    {{ value.title }}
                  </h3>

                  <!-- Flip Indicator -->
                  <div class="mt-2 text-oxo-red/50 group-hover:text-oxo-red transition-colors duration-300 animate-bounce-subtle">
                    <i class="fas fa-info-circle text-lg" aria-hidden="true"></i>
                  </div>
                </div>
              </div>

              <!-- Back Side -->
              <div class="absolute inset-0 backface-hidden rotate-y-180">
                <div class="h-full relative bg-linear-to-br from-oxo-red to-oxo-red/90 p-8 rounded-3xl flex flex-col items-center justify-center text-center shadow-2xl">
                  <div class="mb-4 text-white/40">
                    <i :style="faIconStyle('fas fa-' + value.icon.replace('fa-', '').replace('fas ', ''))" :class="'fas fa-' + value.icon.replace('fa-', '').replace('fas ', '') + ' text-4xl'" aria-hidden="true"></i>
                  </div>
                  <h4 class="font-display font-black text-xl text-white mb-4 uppercase">
                    {{ value.title }}
                  </h4>
                  <p class="text-white text-sm font-medium leading-relaxed">
                    {{ value.description || "Le socle de notre pédagogie et de notre engagement envers nos étudiants." }}
                  </p>
                  
                  <!-- Close Indicator -->
                  <div class="absolute bottom-6 right-6 text-white/50">
                    <i class="fas fa-redo text-xs" aria-hidden="true"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.perspective-1000 {
  perspective: 1000px;
}
.preserve-3d {
  transform-style: preserve-3d;
}
.backface-hidden {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  -moz-backface-visibility: hidden;
  /* Firefox fix: force GPU layer */
  transform: translateZ(0);
}
.rotate-y-180 {
  transform: rotateY(180deg);
}

/* Firefox-specific fix for front face when flipped */
.rotate-y-180 .backface-hidden:first-child {
  visibility: hidden;
}
.rotate-y-180 .backface-hidden:last-child {
  visibility: visible;
}

@keyframes bounce-subtle {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
.animate-bounce-subtle {
  animation: bounce-subtle 2s infinite;
}
</style>
